import { InfiniteData, useInfiniteQuery, useQueryClient, UseInfiniteQueryOptions } from '@tanstack/react-query';
import * as R from 'ramda';
import { useEffect, useMemo, useState } from 'react';

import { RestaurantApiCursorPaginationParams, PaginatedData, PaginatedResults } from 'apis/restaurant-api/types';

type DataOrResults<T> = PaginatedData<T> | PaginatedResults<T>;

const useCursorPagination = <T, P>(
  key: string,
  paginationParams: RestaurantApiCursorPaginationParams<P>,
  queryFunction: (params: RestaurantApiCursorPaginationParams<P>) => Promise<DataOrResults<T>>,
  options: Omit<UseInfiniteQueryOptions<DataOrResults<T>>, 'keepPreviousData'> = {},
) => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState(paginationParams);
  const { limit: _limit, cursor: _cursor, pivotId: requiredItemId, ...newParams } = paginationParams;

  const shouldUpdateList = useMemo(() => {
    // Is the same list still being requested? If not, update the query.
    const { limit: _limit, cursor: _cursor, pivotId: _pivotId, ...previousParams } = query;
    if (!R.equals(previousParams, newParams)) {
      return true;
    }

    // Check if a certain item was requested and if we already have it
    // in the current data. If we don't have it, then update the query.
    //
    // We don't want to fetch a new list if we already have the item
    // available, because that would clear the current list causing user to
    // lose their scroll position in the list.
    //
    // We also don't want to fetch a new list when pivotId is
    // cleared, because we don't want the list to refresh when user closes
    // the item details view.
    if (requiredItemId) {
      const currentData: InfiniteData<unknown> | undefined = queryClient.getQueryData([key, newParams], {
        exact: false,
      });

      const items = currentData?.pages.flatMap((page: any) => page.results || page.data.purchases) || [];
      const currentDataContainsRequiredItem = items.some(item => item.id === requiredItemId);
      if (!currentDataContainsRequiredItem) {
        return true;
      }
    }
    return false;
  }, [requiredItemId, newParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldUpdateList) {
      setQuery(paginationParams);
    }
  }, [shouldUpdateList]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, ...rest } = useInfiniteQuery<
    DataOrResults<T>,
    unknown,
    DataOrResults<T>,
    [string, RestaurantApiCursorPaginationParams<P>]
  >(
    [key, query],
    ({ queryKey, pageParam: cursor }) => {
      const [_, params] = queryKey;
      return queryFunction({ ...params, cursor, pivotId: params.pivotId && !cursor ? params.pivotId : undefined });
    },
    {
      ...options,
      getNextPageParam: lastPage => lastPage.pagination?.next || lastPage.metadata?.next,
      getPreviousPageParam: firstPage => firstPage.pagination?.previous || firstPage.metadata?.previous,
      keepPreviousData: true,
    },
  );
  return {
    ...rest,
    didUpdateList: shouldUpdateList,
    data: data?.pages.flatMap(page => ('results' in page ? page.results : page.data)) || [],
  };
};

export default useCursorPagination;
